import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import PageContent from '@zeus/core/layout/PageContent';
import { updateAlarmRequest } from '_/api/alarms/alarms.api';
import { useAlarmGroupContext } from '_/contexts/AlarmGroupContext';
import { useAlarmsContext } from '_/contexts/AlarmsContext';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { ALARMS_QUERY_KEY } from '_/hooks/useAlarms';
import useOrgAccess from '_/hooks/useOrgAccess';
import { formatWrittenDate } from '_/utils/dateUtils';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Tooltip, Table, Switch, Input, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useState, ReactElement, useMemo, useEffect, useCallback } from 'react';

interface Alarm {
  id: string;
  name: string;
  severity: string;
  createdOn: string;
  active: boolean;
}

function AlarmGroupAlarms(): ReactElement {
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const history = useNavigate();
  const params = useParams({ strict: false }) as Partial<{
    orgSlug: string;
    projectSlug: string;
    flowSlug: string;
  }>;
  const orgSlug = params.orgSlug || '';
  const projectSlug = params.projectSlug || '';
  const flowSlug = params.flowSlug || '';
  const { selectedOrganization } = useOrganization();
  const { selectedProject } = useProject();
  const { selectedFlow } = useFlowContext();
  const { selectedAlarmGroup, alarmGroupId } = useAlarmGroupContext();
  const { alarms: alarmsData } = useAlarmsContext();
  const { setBreadcrumbs, setActionButtons } = useTopHeader();
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Format alarms data to match the table format (similar to getAlarmsTable selector)
  const alarms = useMemo(() => {
    return alarmsData.map((alarm) => ({
      ...alarm,
      createdOn: formatWrittenDate(alarm.addedAt),
      href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmGroups/${alarmGroupId}/alarms/${alarm.id}`,
      linkSupported: true,
    }));
  }, [alarmsData, orgSlug, projectSlug, flowSlug, alarmGroupId]);

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const handleNewAlarm = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      void history({
        to: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmGroups/${alarmGroupId}/alarms/_new`,
      });
    },
    [history, orgSlug, projectSlug, flowSlug, alarmGroupId],
  );

  const handleToggleActiveAlarm = async (e: React.ChangeEvent<HTMLInputElement>, alarm: Alarm) => {
    try {
      // Update alarm via React Query
      await updateAlarmRequest(alarm.id, { active: e.target.checked });

      // Invalidate alarms query to refetch the list
      if (alarmGroupId) {
        void queryClient.invalidateQueries({ queryKey: ALARMS_QUERY_KEY(alarmGroupId) });
      }

      // Show success message
      notification.success({
        message: 'Alarm Updated',
      });
    } catch (error: any) {
      console.error('Error updating alarm', error);
      notification.error({
        message: error.message || 'Error updating alarm',
      });
    }
  };

  const handleRowClick = (record: Alarm) => {
    if (access) {
      void history({
        to: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmGroups/${alarmGroupId}/alarms/${record.id}`,
      });
    }
  };

  // Filter alarms based on search text
  const filteredAlarms = alarms.filter((alarm) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return alarm.name?.toLowerCase().includes(lowerSearch) || alarm.severity?.toLowerCase().includes(lowerSearch);
  });

  const columns: ColumnsType<Alarm> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string, record: Alarm) => (
        <a onClick={() => handleRowClick(record)} style={{ cursor: access ? 'pointer' : 'default' }}>
          {text}
        </a>
      ),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      sorter: (a, b) => a.severity.localeCompare(b.severity),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      sorter: (a, b) => dayjs(a.createdOn).unix() - dayjs(b.createdOn).unix(),
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 120,
      align: 'center',
      render: (active: boolean) => <input type="checkbox" checked={active} disabled readOnly />,
    },
  ];

  const editColumns: ColumnsType<Alarm> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      sorter: (a, b) => a.severity.localeCompare(b.severity),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      sorter: (a, b) => dayjs(a.createdOn).unix() - dayjs(b.createdOn).unix(),
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 120,
      align: 'center',
      render: (active: boolean, record: Alarm) => (
        <input type="checkbox" checked={active} onChange={(e) => handleToggleActiveAlarm(e, record)} disabled={!isEditMode} />
      ),
    },
  ];

  // Set up breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      { title: selectedProject?.name || '', href: `/orgs/${orgSlug}/projects/${projectSlug}/home` },
      { title: 'Flows', href: `/orgs/${orgSlug}/projects/${projectSlug}/flows` },
      {
        title: selectedFlow?.name || 'Flow',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/home`,
      },
      {
        title: 'Alarm Groups',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups`,
      },
      {
        title: selectedAlarmGroup?.name || 'Alarm Group',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups/${alarmGroupId}/home`,
      },
      { title: 'Alarms' },
    ]);

    setActionButtons([
      {
        onClick: handleNewAlarm,
        icon: <PlusCircleOutlined />,
        label: 'Add',
        tooltip: access ? 'Add an Alarm' : 'You do not have the right permissions to add an alarm',
        disabled: !access,
      },
    ]);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
    };
  }, [
    setBreadcrumbs,
    setActionButtons,
    selectedOrganization?.name,
    orgSlug,
    selectedProject?.name,
    projectSlug,
    selectedFlow?.name,
    flowSlug,
    selectedAlarmGroup?.name,
    alarmGroupId,
    access,
    handleNewAlarm,
  ]);

  return (
    <PageContent>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search alarms..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 400 }}
          allowClear
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tooltip
            title={access ? 'Enable/Disable an Alarm' : 'You do not have the right permissions to enable/disable an alarm'}
          >
            <span>
              <Switch checked={isEditMode} onChange={toggleEditMode} disabled={!access} />
            </span>
          </Tooltip>
          <span>Enable/Disable</span>
        </div>
      </div>
      <Table
        columns={isEditMode ? editColumns : columns}
        dataSource={filteredAlarms}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
    </PageContent>
  );
}

export default AlarmGroupAlarms;
